import { useState, useCallback, useEffect, useMemo } from "react";
import Button from "../../Button/Button";
import CheckIcon from "../../../assets/svg/check-icon";
import SearchIcon from "../../../assets/svg/search-icon";
import { useGetLeadUniversitiesQuery } from "../../../redux/services/crmApi";
import { SearchableDropdownOption } from "../../SearchableDropdown/types";
import {
  selectCrmLeadsFilters,
  clearCrmLeadsUniversity,
  setCrmLeadsUniversity,
  selectCommunicationsFilters,
  clearCommunicationsUniversity,
  setCommunicationsUniversity,
} from "../../../redux/slices/filterSlice";
import {
  setPmsUniversity,
  clearPmsUniversity,
  selectPmsPropertiesFilters,
} from "../../../redux/slices/pmsFilterSlice";
import { useDispatch, useSelector } from "react-redux";

interface University {
  id: string;
  name: string;
}

const UniversityFilter = ({
  onClose,
  isCommunication = false,
  isPms = false,
}: {
  onClose: () => void;
  isCommunication?: boolean;
  isPms?: boolean;
}) => {
  const leadsFilters = useSelector(selectCrmLeadsFilters);
  const communicationsFilters = useSelector(selectCommunicationsFilters);
  const pmsFilters = useSelector(selectPmsPropertiesFilters);

  const destinationCity = isPms
    ? pmsFilters?.city
    : isCommunication
    ? communicationsFilters?.city
    : leadsFilters?.destinationCity;

  const selectedUniversity = isPms
    ? pmsFilters?.university
    : isCommunication
    ? communicationsFilters?.university
    : leadsFilters?.university;

  const [searchQuery, setSearchQuery] = useState("");
  const [filteredUniversities, setFilteredUniversities] = useState<
    SearchableDropdownOption[]
  >([]);

  // Helper to get single value from selectedUniversity (handles both string and array formats)
  const getSingleValue = (val: string | string[] | undefined): string => {
    if (!val) return "";
    if (Array.isArray(val)) return val[0] || "";
    return val;
  };

  const [selectedUniversityId, setSelectedUniversityId] = useState<{
    value: string;
    displayValue: string;
  }>({
    value: getSingleValue(selectedUniversity.value as string | string[]),
    displayValue: getSingleValue(selectedUniversity.displayValue as string | string[]),
  });
  const dispatch = useDispatch();

  // For PMS, use pmsFilters.city.id directly
  const cityIdsForQuery = isPms
    ? pmsFilters?.city?.id
      ? [String(pmsFilters.city.id)]
      : []
    : Array.isArray(destinationCity?.value)
    ? destinationCity.value
    : destinationCity?.value
    ? [destinationCity.value]
    : [];

  const {
    data: universities,
    isLoading,
    isFetching,
  } = useGetLeadUniversitiesQuery(cityIdsForQuery, {
    skip: cityIdsForQuery.length === 0,
  });

  const transformedUniversities = useMemo(
    () =>
      universities?.map((university: University) => ({
        value: university.id,
        label: university.name,
      })) || [],
    [universities]
  );

  // Handle search functionality
  const handleSearch = useCallback(
    (query: string) => {
      if (!query.trim()) {
        setFilteredUniversities(transformedUniversities);
        return;
      }

      const filtered = transformedUniversities.filter(
        (university: SearchableDropdownOption) =>
          university.label
            ?.toString()
            .toLowerCase()
            .includes(query.toLowerCase())
      );
      setFilteredUniversities(filtered);
    },
    [transformedUniversities]
  );

  // Debounced search
  useEffect(() => {
    handleSearch(searchQuery);
  }, [searchQuery, handleSearch]);

  // Initialize filtered countries
  useEffect(() => {
    setFilteredUniversities(transformedUniversities);
  }, [transformedUniversities]);

  const handleUniversitySelect = (
    universityId: string,
    universityLabel: string
  ) => {
    if (universityId === selectedUniversityId.value) {
      setSelectedUniversityId({ value: "", displayValue: "" });
      dispatch(
        isCommunication
          ? clearCommunicationsUniversity()
          : clearCrmLeadsUniversity()
      );
      onClose();
    } else {
      setSelectedUniversityId({
        value: universityId,
        displayValue: universityLabel,
      });
    }
  };

  const handleApply = () => {
    if (selectedUniversityId.value === getSingleValue(selectedUniversity.value as string | string[])) {
      return;
    }

    if (isPms) {
      dispatch(
        setPmsUniversity({
          id: selectedUniversityId.value ? Number(selectedUniversityId.value) : null,
          value: selectedUniversityId.value as string,
          displayValue: selectedUniversityId.displayValue as string,
        })
      );
    } else if (isCommunication) {
      dispatch(
        setCommunicationsUniversity({
          value: selectedUniversityId.value as string,
          displayValue: selectedUniversityId.displayValue as string,
        })
      );
    } else {
      dispatch(
        setCrmLeadsUniversity({
          value: selectedUniversityId.value as string,
          displayValue: selectedUniversityId.displayValue as string,
        })
      );
    }
    onClose();
  };

  return (
    <div className="flex flex-col h-full max-h-[90vh]">
      {/* Search Input */}
      <div className="relative border-b border-border-gray dark:border-border-gray-dk shrink-0">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-4 w-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search university"
          className="w-full pl-10 pr-4 py-2 text-sm outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
        />
      </div>

      {/* Selected Country Display */}
      {selectedUniversityId.value && (
        <div
          className="flex items-center justify-between p-3 bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk border-b border-border-gray dark:border-border-gray-dk shrink-0 cursor-pointer"
          onClick={() =>
            setSelectedUniversityId({ value: "", displayValue: "" })
          }
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
              {
                transformedUniversities.find(
                  (c: SearchableDropdownOption) =>
                    c.value === selectedUniversityId.value
                )?.label
              }
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk" />
          </div>
        </div>
      )}

      {/* Countries List */}
      <div className="flex flex-col flex-1 min-h-0">
        <div className="px-3 py-2 text-xs font-semibold text-dark-gray dark:text-dark-gray-dk uppercase border-b border-border-gray dark:border-border-gray-dk">
          All Universities
        </div>

        <div className="overflow-y-auto">
          {isLoading || isFetching ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                {isLoading
                  ? "Loading universities..."
                  : "Fetching universities..."}
              </div>
            </div>
          ) : filteredUniversities.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                {searchQuery.trim()
                  ? "No universities found"
                  : "No universities available"}
              </div>
            </div>
          ) : (
            filteredUniversities
              ?.filter(
                (university: SearchableDropdownOption) =>
                  university.value !== selectedUniversityId.value
              )
              ?.map((university: SearchableDropdownOption) => (
                <div
                  key={university.value}
                  onClick={() =>
                    handleUniversitySelect(
                      university.value,
                      university.label as string
                    )
                  }
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-hover-gray dark:hover:bg-hover-gray-dk border-b border-border-gray dark:border-border-gray-dk last:border-b-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-md text-heading-text dark:text-heading-text-dk truncate">
                      {university.label}
                    </div>
                  </div>
                  {selectedUniversityId.value === university.value && (
                    <CheckIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk shrink-0 ml-2" />
                  )}
                </div>
              ))
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mt-4 px-3 pb-2">
        <Button
          innerText="Cancel"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
          onClick={onClose}
        />
        <Button
          innerText="Apply"
          disabled={selectedUniversityId.value === getSingleValue(selectedUniversity.value as string | string[])}
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          onClick={handleApply}
        />
      </div>
    </div>
  );
};

export default UniversityFilter;
